import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { OnlineImportResult } from './onlineImportService';

/** Step 3: what the import did — counts, and the rows that did not go in, by their row in the file. */
export function OnlineImportResultStep({ result }: { result: OnlineImportResult }) {
  const { t } = useTranslation();
  const written = result.created + result.updated;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <Count value={result.created} label={t('onlineImport.result.created', { defaultValue: 'Created' })} />
        <Count value={result.updated} label={t('onlineImport.result.updated', { defaultValue: 'Updated' })} />
        <Count value={result.failed} label={t('onlineImport.result.failed', { defaultValue: 'Failed' })} />
      </Box>
      <Alert severity={result.failed === 0 ? 'success' : written > 0 ? 'warning' : 'error'}>
        {result.failed === 0
          ? t('onlineImport.result.completed', { defaultValue: 'Import completed.' })
          : t('onlineImport.result.completedWithFailures', {
            defaultValue: 'Import completed. {{count}} rows did not go in; they are listed below and in the run.', count: result.failed,
          })}
      </Alert>
      {result.failedRows.length > 0 && (
        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t('onlineImport.failedRows', { defaultValue: 'Rows that did not go in' })}
          </Typography>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>{t('onlineImport.row', { defaultValue: 'Row' })}</TableCell>
                <TableCell>{t('onlineImport.reason', { defaultValue: 'Reason' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.failedRows.map((row) => (
                <TableRow key={row.rowNumber}>
                  <TableCell>{row.rowNumber}</TableCell>
                  <TableCell>{row.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

function Count({ value, label }: { value: number; label: string }) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.25, py: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: 22, fontWeight: 500, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
